import { FormControl, Input, MessageBlock, Textarea } from '@deckee/deck-hand';

import classNames from 'classnames/bind';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as React from 'react';
import * as yup from 'yup';
import {
  SHARING_MESSAGE__ERROR,
  SHARING_MESSAGE__LOADING,
} from '../../../configurations/constants';
import captureSpecificError from '../../../services/capture-error';
import { captureEvent } from '../../../services/event-capture';
import { submitShare } from '../../../services/share';
import { sharingMessageSuccess } from '../../helpers/share-helpers';
import HeaderBar from '../../layouts/components/HeaderBar/HeaderBar';
import styles from './SharingDialog.scss';
import SubmitShare from './SubmitShare';

const cx = classNames.bind(styles);
const InviteFriendSchema = yup.object({
  email: yup
    .string('Enter your email')
    .required('Email is required')
    .email('Enter a valid email'),
  comment: yup.string('A comment should be text').notRequired(),
});

const InviteFriend = ({
  path,
  title,
  description,
  type,
  id,
  onSuccess,
  submissionSuccess,
}) => {
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const captureEventData = React.useCallback(
    async (eventName) => {
      await captureEvent({
        type: eventName,
        payload: {
          itemType: type,
          itemId: id,
          url: path,
        },
      });
    },
    [id, path, type],
  );

  React.useEffect(() => {
    async function captureLoadEventData() {
      await captureEventData('inviteFriend: load');
    }
    captureLoadEventData();
  }, [captureEventData]);

  const submitHandler = React.useCallback(
    async (values) => {
      try {
        setIsLoading(true);
        await captureEventData('inviteFriend: submit');

        await submitShare({
          path,
          title,
          description,
          type,
          id,
          ...values,
        });

        onSuccess();
      } catch (err) {
        captureSpecificError(
          err,
          'containers/app-main-client/src/components/features/Sharing/InviteFriend.jsx',
        );
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [description, id, path, title, type, onSuccess, captureEventData],
  );

  // flag for all success conditions.
  const showSuccess = !error && !isLoading && submissionSuccess;

  return (
    <div className={cx('sharing-dialog__invite-friend')}>
      <HeaderBar centerNode="Share via Deckee" />
      {isLoading ? (
        <MessageBlock className={cx('sharing-dialog__message-block')}>
          {SHARING_MESSAGE__LOADING}
        </MessageBlock>
      ) : null}

      {error ? (
        <MessageBlock
          className={cx('sharing-dialog__message-block')}
          type="error"
        >
          {SHARING_MESSAGE__ERROR}
        </MessageBlock>
      ) : null}

      {showSuccess ? (
        <MessageBlock
          className={cx('sharing-dialog__message-block')}
          type="success"
        >
          {sharingMessageSuccess(type)}
        </MessageBlock>
      ) : null}

      {!showSuccess ? (
        <Formik
          initialValues={{
            email: '',
            comment: '',
          }}
          initialErrors={{ email: 'required' }}
          validationSchema={InviteFriendSchema}
          onSubmit={submitHandler}
        >
          {({ handleChange, values, isSubmitting, isValid }) => (
            <Form
              className={cx('sharing-dialog__invite-friend-form')}
              autoComplete="off"
            >
              <div className={cx('sharing-dialog__member-and-search')}>
                <div className={cx('sharing-dialog__header-details')}>
                  <FormControl label="Enter email address*">
                    <Input
                      value={values.email}
                      name="email"
                      type="email"
                      onChange={handleChange}
                      required
                    />
                  </FormControl>
                </div>
              </div>

              <FormControl label="Say something about this...">
                <Textarea
                  name="comment"
                  id="comment"
                  rows={3}
                  value={values.comment}
                  onChange={handleChange}
                />
              </FormControl>

              <SubmitShare
                className={cx('sharing-dialog__submit-button')}
                type="submit"
                submitting={isSubmitting}
                label="Invite friend"
                iconName="send"
                disabled={!isValid || isSubmitting}
              />
            </Form>
          )}
        </Formik>
      ) : null}
    </div>
  );
};

InviteFriend.propTypes = {
  path: PropTypes.string.isRequired,
  title: PropTypes.string,
  type: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  submissionSuccess: PropTypes.bool,

  description: PropTypes.string,
  id: PropTypes.string,
};

InviteFriend.defaultProps = {
  id: undefined,
  description: undefined,
  title: undefined,
  submissionSuccess: false,
};

export default InviteFriend;
